import { useModalState } from "@clipboard-health/ui-react";
import { HOME_HEALTH_CASES_PATH } from "@src/appV2/ExperimentalHomeHealth/Cases/paths";
import { useIsHomeHealthEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthEnabled";
import { loadLazily } from "@src/utils/loadLazily";
import { routes } from "@src/appV2/redesign/utils/routes";
import firebase from "firebase/app";
import { useCallback, useEffect, useState } from "react";
import { Redirect, Route, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { type SessionType } from "@src/modules/interface";
import { useInitializeLaunchDarkly } from "@src/hooks/useInitializeLaunchDarkly";
import qs from "query-string";

import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { AdminMenuPriviligesProvider } from "./Shared/Navigation/priviligesProviders/AdminMenuPriviligesProvider";
import { WorkplaceMenuPriviligesProvider } from "./Shared/Navigation/priviligesProviders/WorkplaceMenuPriviligesProvider";
import { signInWithCustomToken } from "@src/containers/enter/firebase";
import { ConnectedOmnisearch } from "@src/containers/app/omnisearch";
import { AppLayoutDesktop } from "./Desktop/AppLayoutDesktop";
import { useIsMobile, useIsTablet } from "@src/appV2/redesign/AppLayout/useResponsive";
import { AppLayoutMobile } from "./Mobile/AppLayoutMobile";
import { WorkerMenuPriviligesProvider } from "./Shared/Navigation/priviligesProviders/WorkerMenuPriviligesProvider";
import { FloatingTabsProvider } from "@src/appV2/redesign/AppLayout/Mobile/FloatingTabsContext";
import { LogoutConfirmationDialog } from "./Shared/LogoutConfirmationDialog";
import { AppLayoutTablet } from "./Tablet/Navigation/AppLayoutTablet";
import { BrazeInitializedProvider } from "@src/appV2/redesign/AppLayout/Shared/BrazeContext";
import { ModalsContainerProvider } from "@src/appV2/redesign/components/useModalsContainerContext";

interface NavigationMenuContainerProps {
  admin: boolean;
  isAdmin: boolean;
  isAgent: boolean;
  isFacility: boolean;
  isFirebaseSession?: boolean;
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  session: any;
  get: () => void;
  getAdminProfile: () => void;
  loadingAdminProfile: boolean;
  initFirebaseSession: () => Promise<void>;
  getLocations: () => void;
  getEmployees: () => void;
}

const AdminAppLogin = loadLazily(
  async () =>
    await import("@src/containers/enter/commonLogin").then((module) => ({
      default: module.AdminAppLogin,
    }))
);

const FirebaseLoginVerify = loadLazily(
  async () =>
    await import("@src/containers/enter/commonLogin").then((module) => ({
      default: module.FirebaseLoginVerify,
    }))
);

export function NavigationMenuContainer(props: NavigationMenuContainerProps): JSX.Element {
  const {
    admin,
    isAdmin,
    isAgent: isWorker,
    isFacility: isWorkplace,
    isFirebaseSession,
    session,
    get,
    getAdminProfile,
    loadingAdminProfile,
    initFirebaseSession,
    getLocations,
    getEmployees,
  } = props;

  const logoutDialog = useModalState();

  const [loading, setLoading] = useState(true);
  const { ready, profile, user } = useSelector((state: SessionType) => state.session || {});
  const { access } = user ?? {};
  const isAuthorized = isAdmin || isWorker || isWorkplace;

  const history = useHistory();
  const location = useLocation();

  // get responsive mode
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Home Health check for start page redirect
  const isHomeHealthEnabled = useIsHomeHealthEnabled();

  // init launch darkly
  useInitializeLaunchDarkly({
    profile,
    isAdmin,
    adminUserId: session.adminUserId,
    workplaceId: isWorkplace ? session.userId : undefined,
  });

  // Initialize admin profile
  useEffect(() => {
    if (loadingAdminProfile) {
      getAdminProfile();
    }
  }, [loadingAdminProfile, getAdminProfile]);

  // Initialize Firebase session
  useEffect(() => {
    async function load() {
      setLoading(true);
      await initFirebaseSession();
      setLoading(false);
    }
    load();
  }, [initFirebaseSession]);

  // Fetch session data and locations
  useEffect(() => {
    if (loading || !ready) {
      return;
    }
    if (!isAuthorized) {
      return;
    }
    get();
    getLocations();

    if (session?.admin) {
      getEmployees();
    }
  }, [isAuthorized, get, getLocations, getEmployees, loading, ready, session?.admin]);

  // init firebase token
  const qString = qs.parse(location.search);
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [isToken, setToken] = useState<any>(qString.token);
  const checkForAccessToken = useCallback(async (token) => {
    const result = await signInWithCustomToken(token);
    if ("error" in result) {
      setToken(false);
    } else {
      setToken(true);
    }
  }, []);

  useEffect(() => {
    if (loading || !ready) {
      return;
    }
    if (isToken) {
      checkForAccessToken(isToken);
    }
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkForAccessToken, loading, ready]);

  // redirect to the login page, if the user is not authenticated and not use a verification link
  useEffect(() => {
    const currentPath = window.location.pathname;

    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    return firebase.auth().onAuthStateChanged((user) => {
      // Skip redirect during the email verification process
      const isVerificationPage = currentPath.includes("login/verify");

      if (!user && !isVerificationPage) {
        history.replace("/login");
      }
    });
  }, [history]);

  function renderStartPage() {
    if (isAdmin) {
      return <Redirect to={routes.adminAccountsAgents} />;
    }

    if (isWorker) {
      return <Redirect to={routes.agentDashboard} />;
    }

    if (isWorkplace) {
      if (isHomeHealthEnabled) {
        return <Redirect to={`/v2/home/${HOME_HEALTH_CASES_PATH}`} />;
      }
      return <Redirect to={routes.facilityDashboard} />;
    }

    return null;
  }

  // Early return for login routes - these should always render
  const isLoginRoute =
    location.pathname === routes.login || location.pathname === routes.loginVerify;

  if (isLoginRoute) {
    return (
      <BerlinThemeProvider>
        <div style={{ height: "100vh" }}>
          <Route exact path={routes.login} component={AdminAppLogin} />
          <Route exact path={routes.loginVerify} component={FirebaseLoginVerify} />
        </div>
      </BerlinThemeProvider>
    );
  }

  // Guard to prevent rendering while session is initializing
  // This matches the old AppLayout logic (line 918 in appLayout.tsx)
  // Prevents API calls from firing before auth token and profile data are ready
  //
  // Important: ready becomes true BEFORE fetchSession() completes loading the full profile
  // We need to wait for profile.tmz to exist as it's required by API calls in AgentDashboard
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  const hasRequiredProfileData = profile && profile.userId;

  if ((loading || !ready) && session.userId) {
    return <></>;
  }

  // If user is logged out (no profile data and no session loading), redirect to login
  // This prevents users from getting stuck on a blank page after logout
  if (!hasRequiredProfileData && !loading) {
    return <Redirect to="/login" />;
  }

  // Additional guard: ensure profile data is fully loaded before rendering
  // AgentDashboard needs profile.tmz, profile.geoLocation, profile.preference, etc.
  if (!hasRequiredProfileData) {
    return <></>;
  }

  // Guard for workplace users: wait for roles (access) to be loaded
  // This prevents a redirect loop where PrivateRoute redirects to /enter because
  // isAuthorized is false (roles not loaded), and /enter redirects back to /facility/dashboard
  // This matches the guard in legacy appLayout.tsx (line 975-977)
  if (isWorkplace && isFirebaseSession && access === undefined) {
    return <></>;
  }

  return (
    <BerlinThemeProvider>
      <ModalsContainerProvider>
        <BrazeInitializedProvider isAdminSession={admin} isWorkplace={isWorkplace}>
          <AdminMenuPriviligesProvider>
            <WorkplaceMenuPriviligesProvider>
              <WorkerMenuPriviligesProvider>
                <FloatingTabsProvider>
                  <ConnectedOmnisearch />
                  {isMobile ? (
                    <AppLayoutMobile
                      isAdmin={isAdmin}
                      isWorker={isWorker}
                      isWorkplace={isWorkplace}
                      logoutConfirm={logoutDialog.openModal}
                      renderStartPage={renderStartPage}
                    />
                  ) : isTablet ? (
                    <AppLayoutTablet
                      isAdmin={isAdmin}
                      isWorker={isWorker}
                      isWorkplace={isWorkplace}
                      logoutConfirm={logoutDialog.openModal}
                      renderStartPage={renderStartPage}
                    />
                  ) : (
                    <AppLayoutDesktop
                      admin={admin}
                      isAdmin={isAdmin}
                      isWorker={isWorker}
                      isWorkplace={isWorkplace}
                      userName={profile.name}
                      userId={profile.userId}
                      logoutConfirm={logoutDialog.openModal}
                      renderStartPage={renderStartPage}
                    />
                  )}
                  <LogoutConfirmationDialog modalState={logoutDialog} />
                </FloatingTabsProvider>
              </WorkerMenuPriviligesProvider>
            </WorkplaceMenuPriviligesProvider>
          </AdminMenuPriviligesProvider>
        </BrazeInitializedProvider>
      </ModalsContainerProvider>
    </BerlinThemeProvider>
  );
}
